import axios from "axios";

// Determine the effective backend base URL.
// On production with a custom domain (e.g. portale.tuodominio.it), the
// REACT_APP_BACKEND_URL baked at build time may still point to the Emergent
// host. Using it would make cookies cross-site and iOS Safari/Chrome would
// silently drop them on every XHR. Detect the current page origin and always
// prefer it when the baked-in URL belongs to a different host; Emergent's
// reverse proxy routes /api requests to the backend on the SAME host.
const BUILD_URL = process.env.REACT_APP_BACKEND_URL || "";
const ORIGIN = typeof window !== "undefined" ? window.location.origin : "";

function resolveBackendUrl() {
  if (!ORIGIN) return BUILD_URL; // SSR / tests
  try {
    const buildHost = new URL(BUILD_URL).host;
    const currentHost = new URL(ORIGIN).host;
    if (!buildHost || buildHost === currentHost) return ORIGIN;
    // Running on a different host (custom domain or local dev with proxy)
    return ORIGIN;
  } catch {
    return ORIGIN;
  }
}

export const BACKEND_URL = resolveBackendUrl();
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Silent session renewal: the access cookie lives 1h, the refresh cookie 7
// days (rotated on each use). On a 401 we call /auth/refresh once and retry
// the original request; if the refresh fails the caller sees the 401.
const NO_REFRESH = [
  "/auth/login",
  "/auth/refresh",
  "/auth/logout",
  "/auth/register",
  "/auth/verify-email",
  "/auth/reset-password",
  "/auth/forgot-password",
];
let refreshPromise = null;

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const { config, response } = error;
    const url = config?.url || "";
    if (
      response?.status !== 401 ||
      config?._retried ||
      NO_REFRESH.some((p) => url.startsWith(p))
    ) {
      return Promise.reject(error);
    }
    try {
      if (!refreshPromise) {
        refreshPromise = api
          .post("/auth/refresh")
          .finally(() => {
            refreshPromise = null;
          });
      }
      await refreshPromise;
    } catch {
      return Promise.reject(error);
    }
    return api({ ...config, _retried: true });
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Qualcosa è andato storto. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) =>
        e && typeof e.msg === "string"
          ? e.msg.replace(/^Value error,\s*/i, "")
          : JSON.stringify(e)
      )
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function apiError(e) {
  return (
    formatApiErrorDetail(e?.response?.data?.detail) ||
    e?.message ||
    "Errore di rete"
  );
}
